import { useEffect, useRef, useState } from "react";
import { api, getAxiosErrorMessage, API_BASE } from "@/lib/api";
import {
  Upload, Loader2, FileSpreadsheet, Download, CheckCircle2, AlertTriangle,
  CopyPlus, History, X, ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Import en masse de fournisseurs, en trois étapes : dépôt du fichier,
 * prévisualisation, rapport. Aucune écriture n'a lieu avant la confirmation
 * explicite de l'étape 2.
 */

const MODES = [
  {
    value: "ignore",
    label: "Ignorer les doublons",
    help: "Les fiches déjà présentes ne sont pas touchées. Option la plus sûre.",
  },
  {
    value: "update",
    label: "Compléter les fiches existantes",
    help: "Les champs vides du fichier n'effacent jamais une valeur déjà saisie.",
  },
  {
    value: "create",
    label: "Créer malgré tout",
    help: "Crée une seconde fiche pour chaque doublon. À réserver aux cas volontaires.",
  },
];

function StatTile({ value, label, tone, testId }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
  };
  return (
    <div className={`rounded-xl border p-3 text-center ${tones[tone]}`} data-testid={testId}>
      <div className="text-2xl font-bold font-heading tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-wider font-semibold mt-0.5">{label}</div>
    </div>
  );
}

export default function SupplierImportDialog({ open, onOpenChange, onImported }) {
  const [step, setStep] = useState("upload"); // upload | preview | report
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [mode, setMode] = useState("ignore");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setStep("upload");
    setPreview(null);
    setReport(null);
    setMode("ignore");
    setShowHistory(false);
    setBusy(false);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/suppliers/import/history");
      setHistory(data);
      setShowHistory(true);
    } catch (e) {
      toast.error(getAxiosErrorMessage(e));
    }
  };

  const downloadTemplate = (format) => {
    // Téléchargement via une navigation : le cookie HttpOnly est joint par le
    // navigateur, aucun jeton ne transite par l'URL.
    window.open(`${API_BASE}/suppliers/import/template?format=${format}`, "_blank");
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/suppliers/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStep("preview");
    } catch (e) {
      toast.error(getAxiosErrorMessage(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/suppliers/import/commit", {
        import_token: preview.import_token,
        mode,
      });
      setReport(data);
      setStep("report");
      toast.success("Import terminé");
      onImported?.();
    } catch (e) {
      toast.error(getAxiosErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-blue-600" />
            Import en masse de fournisseurs
          </DialogTitle>
        </DialogHeader>

        {/* ---------- Étape 1 : dépôt ---------- */}
        {step === "upload" ? (
          <div className="space-y-4">
            <div className="rounded-xl bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-100 p-4">
              <div className="text-sm text-slate-700">
                Déposez un fichier <strong>.xlsx</strong> ou <strong>.csv</strong>.
                La colonne <strong>Nom</strong> est obligatoire, toutes les autres
                sont facultatives. Rien n'est enregistré avant votre confirmation.
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button" variant="outline" className="rounded-xl h-9"
                  onClick={() => downloadTemplate("xlsx")}
                  data-testid="supplier-import-template-xlsx"
                >
                  <Download size={14} className="mr-1.5" /> Modèle Excel
                </Button>
                <Button
                  type="button" variant="outline" className="rounded-xl h-9"
                  onClick={() => downloadTemplate("csv")}
                  data-testid="supplier-import-template-csv"
                >
                  <Download size={14} className="mr-1.5" /> Modèle CSV
                </Button>
                <Button
                  type="button" variant="ghost" className="rounded-xl h-9"
                  onClick={loadHistory}
                  data-testid="supplier-import-history-btn"
                >
                  <History size={14} className="mr-1.5" /> Historique
                </Button>
              </div>
            </div>

            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xlsm,.csv,.txt"
              className="hidden"
              onChange={(e) => uploadFile(e.target.files?.[0])}
              data-testid="supplier-import-file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="w-full rounded-2xl border-2 border-dashed border-slate-200 hover:border-blue-400 hover:bg-blue-50/40 transition-colors py-10 text-center disabled:opacity-60"
              data-testid="supplier-import-dropzone"
            >
              {busy ? (
                <Loader2 className="h-7 w-7 animate-spin text-blue-600 mx-auto" />
              ) : (
                <Upload size={28} className="mx-auto text-slate-300" strokeWidth={1.5} />
              )}
              <div className="mt-2.5 text-sm font-semibold text-slate-700">
                {busy ? "Analyse du fichier…" : "Choisir un fichier"}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                10 Mo et 10 000 lignes maximum
              </div>
            </button>

            {showHistory ? (
              <div className="rounded-xl border border-slate-100 overflow-hidden" data-testid="supplier-import-history">
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-100">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Historique des imports
                  </span>
                  <button onClick={() => setShowHistory(false)} className="text-slate-400 hover:text-slate-600">
                    <X size={14} />
                  </button>
                </div>
                {history.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-slate-500">
                    Aucun import réalisé pour le moment.
                  </div>
                ) : (
                  <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                    {history.map((h) => (
                      <div key={h.id} className="px-3 py-2 text-sm flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-slate-800 truncate">{h.filename}</div>
                          <div className="text-xs text-slate-400">
                            {new Date(h.created_at).toLocaleString("fr-FR")} · {h.user_name}
                          </div>
                        </div>
                        <div className="text-xs text-slate-600 whitespace-nowrap tabular-nums">
                          <span className="text-emerald-600 font-semibold">{h.created}</span> créés ·{" "}
                          <span className="text-blue-600 font-semibold">{h.updated}</span> maj ·{" "}
                          <span className="text-amber-600 font-semibold">{h.ignored}</span> ignorés ·{" "}
                          <span className="text-rose-600 font-semibold">{h.errors}</span> erreurs
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ---------- Étape 2 : prévisualisation ---------- */}
        {step === "preview" && preview ? (
          <div className="space-y-4" data-testid="supplier-import-preview">
            <div className="text-sm text-slate-600">
              <strong>{preview.filename}</strong> · {preview.total_rows} ligne(s) analysée(s).
              Aucune écriture n'a encore eu lieu.
            </div>

            <div className="grid grid-cols-3 gap-3">
              <StatTile value={preview.to_create} label="À créer" tone="emerald" testId="supplier-import-stat-create" />
              <StatTile value={preview.duplicates} label="Doublons" tone="amber" testId="supplier-import-stat-duplicates" />
              <StatTile value={preview.errors} label="Erreurs" tone="rose" testId="supplier-import-stat-errors" />
            </div>

            {preview.unknown_columns?.length ? (
              <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs text-slate-500">
                Colonnes non reconnues, ignorées :{" "}
                <span className="font-medium text-slate-700">{preview.unknown_columns.join(", ")}</span>
              </div>
            ) : null}

            {preview.duplicates > 0 ? (
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
                  Que faire des doublons ?
                </Label>
                <div className="mt-2 space-y-2">
                  {MODES.map((m) => (
                    <label
                      key={m.value}
                      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        mode === m.value
                          ? "border-blue-300 bg-blue-50/60"
                          : "border-slate-100 hover:bg-slate-50"
                      }`}
                      data-testid={`supplier-import-mode-${m.value}`}
                    >
                      <input
                        type="radio" name="import-mode" value={m.value}
                        checked={mode === m.value}
                        onChange={() => setMode(m.value)}
                        className="mt-1 accent-blue-600"
                      />
                      <div>
                        <div className="text-sm font-semibold text-slate-800">{m.label}</div>
                        <div className="text-xs text-slate-500">{m.help}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}

            {preview.sample_errors?.length ? (
              <div className="rounded-xl border border-rose-100 overflow-hidden">
                <div className="px-3 py-2 bg-rose-50 text-xs font-bold uppercase tracking-wider text-rose-700 flex items-center gap-1.5">
                  <AlertTriangle size={13} /> Lignes en erreur (non importées)
                </div>
                <div className="max-h-40 overflow-y-auto divide-y divide-rose-50">
                  {preview.sample_errors.map((e) => (
                    <div key={e.line} className="px-3 py-1.5 text-xs">
                      <span className="font-semibold text-slate-700">Ligne {e.line}</span>
                      {e.name ? <span className="text-slate-500"> · {e.name}</span> : null}
                      <span className="text-rose-600"> — {e.problems.join(" ; ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {preview.sample_duplicates?.length ? (
              <div className="rounded-xl border border-amber-100 overflow-hidden">
                <div className="px-3 py-2 bg-amber-50 text-xs font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1.5">
                  <CopyPlus size={13} /> Doublons détectés
                </div>
                <div className="max-h-40 overflow-y-auto divide-y divide-amber-50">
                  {preview.sample_duplicates.map((d) => (
                    <div key={d.line} className="px-3 py-1.5 text-xs">
                      <span className="font-semibold text-slate-700">Ligne {d.line}</span>
                      <span className="text-slate-500"> · {d.name}</span>
                      <span className="text-amber-700"> — {d.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {preview.truncated ? (
              <div className="text-xs text-slate-400">
                Seules les {200} premières lignes de chaque catégorie sont détaillées ;
                les compteurs ci-dessus portent sur la totalité du fichier.
              </div>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setStep("upload")}>
                <ArrowLeft size={14} className="mr-1.5" /> Changer de fichier
              </Button>
              <Button
                type="button"
                onClick={commit}
                disabled={busy || (preview.to_create === 0 && (preview.duplicates === 0 || mode === "ignore"))}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                data-testid="supplier-import-confirm-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Lancer l'import
              </Button>
            </DialogFooter>
          </div>
        ) : null}

        {/* ---------- Étape 3 : rapport ---------- */}
        {step === "report" && report ? (
          <div className="space-y-4" data-testid="supplier-import-report">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 size={18} />
              <span className="font-semibold font-heading">Import terminé</span>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <StatTile value={report.created} label="Créés" tone="emerald" testId="supplier-import-report-created" />
              <StatTile value={report.updated} label="Mis à jour" tone="blue" testId="supplier-import-report-updated" />
              <StatTile value={report.ignored} label="Ignorés" tone="amber" testId="supplier-import-report-ignored" />
              <StatTile value={report.errors} label="Erreurs" tone="rose" testId="supplier-import-report-errors" />
            </div>
            {report.error_details?.length ? (
              <div className="rounded-xl border border-rose-100 overflow-hidden">
                <div className="px-3 py-2 bg-rose-50 text-xs font-bold uppercase tracking-wider text-rose-700">
                  Lignes non importées
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-rose-50">
                  {report.error_details.map((e) => (
                    <div key={e.line} className="px-3 py-1.5 text-xs">
                      <span className="font-semibold text-slate-700">Ligne {e.line}</span>
                      {e.name ? <span className="text-slate-500"> · {e.name}</span> : null}
                      <span className="text-rose-600"> — {e.problems.join(" ; ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={reset}>Nouvel import</Button>
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                data-testid="supplier-import-close-btn"
              >
                Fermer
              </Button>
            </DialogFooter>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
